import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchRequests } from '../../../Requests/getRequests';
import { authStaff } from '../../../utils/constants';
import { selectAccessToken } from '../../../slices/staffSlice';
import { show } from '../../../slices/toastSlice';
import { Topbar, Sidebar, Button, Loader } from '../../../components';
import { parseRecipients, smsSegments, MAX_RECIPIENTS } from '../../../utils/sms';
import { HugeiconsIcon } from '@hugeicons/react';
import { Message01Icon, Upload01Icon, Cancel01Icon } from '@hugeicons/core-free-icons';
import ClientMessages from './ClientMessages';
import { TONE_CLASS, deliveryStatus, localPhone } from './smsStatus';

// datetime-local ("2026-08-01T14:30") -> mNotify format ("2026-08-01 14:30")
const toScheduleFormat = (v) => (v ? v.replace('T', ' ').slice(0, 16) : '');

function StatPill({ label, value }) {
    return (
        <div className="flex flex-col">
            <span className="text-xs text-gray-400 uppercase tracking-wide">{label}</span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{value}</span>
        </div>
    );
}

function SendSMS() {
    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [tab, setTab] = useState('compose');

    // compose state
    const [message, setMessage] = useState('');
    const [mode, setMode] = useState('manual');        // 'manual' | 'csv'
    const [manualText, setManualText] = useState('');
    const [csvText, setCsvText] = useState('');
    const [csvName, setCsvName] = useState('');
    const [isSchedule, setIsSchedule] = useState(false);
    const [scheduleDate, setScheduleDate] = useState('');
    const [balance, setBalance] = useState(null);
    const [provider, setProvider] = useState(null); // active SMS gateway (from /sms/balance)
    const [showConfirm, setShowConfirm] = useState(false);
    const [sending, setSending] = useState(false);
    const fileRef = useRef(null);

    // history state
    const [history, setHistory] = useState(null);
    const [report, setReport] = useState(null); // { id, rows } | null

    const toast = useCallback((type, message) => dispatch(show({ type, state: true, message })), [dispatch]);
    const guard401 = useCallback((res) => {
        if (res?.code === 401) { sessionStorage.clear(); navigate('/login'); return true; }
        return false;
    }, [navigate]);

    const loadBalance = useCallback(async () => {
        const res = await fetchRequests('/auth/sms/balance', 'get', authStaff(accessToken), {}, {});
        if (guard401(res)) return;
        if (res?.provider) setProvider(res.provider); // shown even if the balance lookup failed
        if (!res?.err) setBalance(res);
    }, [accessToken, guard401]);

    useEffect(() => {
        document.title = `Send SMS - ${process.env.REACT_APP_SITE_NAME}`;
        loadBalance();
    }, [loadBalance]);

    const loadHistory = useCallback(async () => {
        setHistory(null);
        const res = await fetchRequests('/auth/sms/history', 'get', authStaff(accessToken), {}, {});
        if (guard401(res)) return;
        setHistory(res?.campaigns || []);
    }, [accessToken, guard401]);

    useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, loadHistory]);

    // --- derived ---
    const recipientText = mode === 'manual' ? manualText : csvText;
    const parsed = parseRecipients(recipientText);
    const seg = smsSegments(message);
    const creditsNeeded = seg.segments * parsed.valid.length;
    const available = balance ? (balance.balance || 0) + (balance.bonus || 0) : null;
    const providerLabel = provider ? ({ mnotify: 'mNotify', arkesel: 'Arkesel' }[provider] || provider) : null;
    const overCap = parsed.valid.length > MAX_RECIPIENTS;
    const insufficient = available !== null && creditsNeeded > available;

    const canSend =
        message.trim().length > 0 &&
        parsed.valid.length > 0 &&
        !overCap &&
        (!isSchedule || !!scheduleDate);

    const onPickCsv = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setCsvName(file.name);
        const reader = new FileReader();
        reader.onload = () => setCsvText(String(reader.result || ''));
        reader.readAsText(file);
    };

    const openConfirm = () => {
        if (!canSend) return;
        if (isSchedule && new Date(scheduleDate).getTime() <= Date.now()) {
            toast('error', 'Schedule date must be in the future.');
            return;
        }
        setShowConfirm(true);
    };

    const doSend = async () => {
        setSending(true);
        const body = {
            message: message.trim(),
            recipients: parsed.valid,
            source: mode,
            isSchedule,
            scheduleDate: isSchedule ? toScheduleFormat(scheduleDate) : '',
        };
        const res = await fetchRequests('/auth/sms/send', 'post', authStaff(accessToken), {}, body);
        setSending(false);
        if (guard401(res)) return;
        if (res?.err) { toast('error', res.err); return; }

        setShowConfirm(false);
        const skipped = res.skipped ? ` (${res.skipped.invalid} invalid, ${res.skipped.duplicates} duplicate skipped)` : '';
        toast('success', isSchedule
            ? `SMS scheduled for ${res.recipientCount} recipient(s)${skipped}`
            : `SMS sent to ${res.recipientCount} recipient(s), ${res.creditUsed} credits used${skipped}`);

        setMessage(''); setManualText(''); setCsvText(''); setCsvName('');
        setIsSchedule(false); setScheduleDate('');
        if (fileRef.current) fileRef.current.value = '';
        loadBalance();
    };

    const viewReport = async (id) => {
        setReport({ id, rows: null });
        const res = await fetchRequests(`/auth/sms/history/${id}/report`, 'get', authStaff(accessToken), {}, {});
        if (guard401(res)) return;
        if (res?.err) { toast('error', res.err); setReport(null); return; }
        setReport({ id, rows: res.report || [] });
    };

    return (
        <div className="main-container">
            <Topbar />
            <Sidebar />
            <main className="main-content">
                <section className="section-container space-y-6 md:px-2">

                    {/* Header */}
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-[#1e3a6e]/10 text-[#1e3a6e] dark:text-blue-400 flex items-center justify-center">
                                <HugeiconsIcon icon={Message01Icon} size={20} />
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Send SMS</h1>
                                <p className="text-sm text-gray-400">
                                    Send a bulk message to clients{providerLabel ? <> via <span className="font-medium text-gray-500 dark:text-gray-300">{providerLabel}</span></> : ''}.
                                </p>
                            </div>
                        </div>
                        <div className="text-right">
                            {providerLabel && (
                                <span className="inline-flex items-center gap-1.5 mb-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-600 border border-indigo-100 dark:bg-indigo-500/10 dark:border-indigo-500/20">
                                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                                    Provider: {providerLabel}
                                </span>
                            )}
                            {balance && (
                                <>
                                    <p className="text-xs text-gray-400 uppercase tracking-wide">SMS Balance</p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                                        {available?.toLocaleString()} <span className="text-gray-400 font-normal">({balance.balance?.toLocaleString()} + {balance.bonus?.toLocaleString()} bonus)</span>
                                    </p>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-1 border-b border-gray-100 dark:border-gray-700">
                        {[['compose', 'Compose'], ['history', 'History'], ['messages', 'Client Messages']].map(([t, tabLabel]) => (
                            <button key={t} onClick={() => setTab(t)}
                                className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === t
                                    ? 'border-[#1e3a6e] text-[#1e3a6e] dark:text-blue-400 dark:border-blue-400'
                                    : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
                                {tabLabel}
                            </button>
                        ))}
                    </div>

                    {tab === 'compose' ? (
                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 sm:p-8 space-y-7">

                            {/* Sender */}
                            <div>
                                <label className="form-label">Sender ID</label>
                                <input className="form-input bg-gray-50 dark:bg-gray-900/40" value={process.env.REACT_APP_SMS_SENDER_ID || 'CedisPay'} disabled readOnly />
                                <p className="text-xs text-gray-400 mt-2">Fixed, pre-approved sender ID.</p>
                            </div>

                            {/* Message */}
                            <div>
                                <label className="form-label">Message</label>
                                <textarea className="form-input h-auto py-3 leading-relaxed min-h-[132px]" rows={5} value={message}
                                    onChange={(e) => setMessage(e.target.value)} placeholder="Type your message…" />
                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                    <span>{seg.length} chars</span>
                                    <span>{seg.segments} segment{seg.segments === 1 ? '' : 's'}</span>
                                    <span>{seg.encoding}{seg.unicode ? ' (Unicode — fewer chars/segment)' : ''}</span>
                                </div>
                            </div>

                            {/* Recipients */}
                            <div>
                                <label className="form-label">Recipients</label>
                                <div className="flex gap-2 mt-2 mb-3">
                                    {[['manual', 'Manual entry'], ['csv', 'CSV upload']].map(([m, lbl]) => (
                                        <button key={m} type="button" onClick={() => setMode(m)}
                                            className={`px-3.5 py-2 text-xs font-medium rounded-md border ${mode === m
                                                ? 'bg-[#1e3a6e] text-white border-[#1e3a6e]'
                                                : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                                            {lbl}
                                        </button>
                                    ))}
                                </div>

                                {mode === 'manual' ? (
                                    <textarea className="form-input mt-0 h-auto py-3 leading-relaxed min-h-[104px]" rows={4} value={manualText}
                                        onChange={(e) => setManualText(e.target.value)}
                                        placeholder="Enter numbers separated by commas, e.g. 0241234567, 0209876543" />
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-gray-300 dark:border-gray-600 text-sm text-gray-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <HugeiconsIcon icon={Upload01Icon} size={16} />
                                            {csvName || 'Choose CSV file'}
                                            <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={onPickCsv} />
                                        </label>
                                        {csvName && (
                                            <button type="button" className="text-xs text-red-500" onClick={() => { setCsvText(''); setCsvName(''); if (fileRef.current) fileRef.current.value = ''; }}>Clear</button>
                                        )}
                                    </div>
                                )}

                                {/* recipient summary */}
                                {(recipientText.trim().length > 0) && (
                                    <div className="flex flex-wrap items-center gap-4 mt-3 text-xs">
                                        <span className="text-emerald-600 dark:text-emerald-400 font-medium">{parsed.valid.length} valid</span>
                                        {parsed.invalid.length > 0 && <span className="text-red-500">{parsed.invalid.length} invalid</span>}
                                        {parsed.duplicates > 0 && <span className="text-amber-500">{parsed.duplicates} duplicate removed</span>}
                                        {overCap && <span className="text-red-500 font-semibold">Exceeds {MAX_RECIPIENTS} limit</span>}
                                    </div>
                                )}
                            </div>

                            {/* Schedule */}
                            <div className="flex flex-wrap items-center gap-3">
                                <label className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                    <input type="checkbox" checked={isSchedule} onChange={(e) => setIsSchedule(e.target.checked)} />
                                    Schedule for later
                                </label>
                                {isSchedule && (
                                    <input type="datetime-local" className="form-input mt-0 w-auto" value={scheduleDate}
                                        min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                                        onChange={(e) => setScheduleDate(e.target.value)} />
                                )}
                            </div>

                            {insufficient && (
                                <p className="text-xs text-red-500">Estimated {creditsNeeded.toLocaleString()} credits needed, but only {available?.toLocaleString()} available.</p>
                            )}

                            <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                                <Button btnText={isSchedule ? 'Schedule SMS' : 'Send SMS'} btnType="button"
                                    btnClasses="form-button w-auto px-6 mt-4" btnFunction={openConfirm} disabled={!canSend} />
                            </div>
                        </div>
                    ) : tab === 'history' ? (
                        <HistoryTable history={history} onReport={viewReport} />
                    ) : (
                        <ClientMessages onExpired={guard401} />
                    )}
                </section>
            </main>

            {/* Confirm modal */}
            {showConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !sending && setShowConfirm(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">Confirm {isSchedule ? 'schedule' : 'send'}</h3>
                        <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words max-h-40 overflow-auto">{message}</div>
                        <div className="grid grid-cols-2 gap-3">
                            <StatPill label="Recipients" value={parsed.valid.length.toLocaleString()} />
                            <StatPill label="Segments" value={seg.segments} />
                            <StatPill label="Est. credits" value={creditsNeeded.toLocaleString()} />
                            <StatPill label="Balance" value={available !== null ? available.toLocaleString() : '—'} />
                        </div>
                        {isSchedule && <p className="text-xs text-gray-500">Scheduled for <span className="font-medium">{toScheduleFormat(scheduleDate)}</span></p>}
                        {insufficient && <p className="text-xs text-red-500 font-medium">Insufficient credit — the send may be rejected.</p>}
                        <div className="flex justify-end gap-2 pt-2">
                            <button className="px-4 py-2 text-sm rounded-md border border-gray-200 dark:border-gray-700 text-gray-600" disabled={sending} onClick={() => setShowConfirm(false)}>Cancel</button>
                            <Button btnText={isSchedule ? 'Schedule' : 'Send now'} btnType="button" btnClasses="form-button w-auto px-5" btnFunction={doSend} processing={sending} />
                        </div>
                    </div>
                </div>
            )}

            {/* Delivery report modal */}
            {report && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setReport(null)}>
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">Delivery report</h3>
                            <button onClick={() => setReport(null)}><HugeiconsIcon icon={Cancel01Icon} size={18} className="text-gray-400" /></button>
                        </div>
                        {report.rows === null ? <Loader /> : report.rows.length === 0 ? (
                            <p className="text-sm text-gray-400">No delivery data yet. Check back shortly.</p>
                        ) : (
                            <DeliveryReport rows={report.rows} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// Provider sends "2026-11-02 15:37:31"; shown as sent, with no timezone shift.
const sentAt = (v) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(v || ''));
    if (!m) return v || '—';
    const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m[2]) - 1];
    return `${Number(m[3])} ${month} ${m[1]}, ${m[4]}:${m[5]}`;
};

function DeliveryReport({ rows }) {
    const withStatus = rows.map((r) => ({ ...r, st: deliveryStatus(r.status) }));
    const sorted = [...withStatus].sort((a, b) => a.st.rank - b.st.rank);
    const count = (tone) => withStatus.filter((r) => r.st.tone === tone).length;
    const failed = count('bad');
    const waiting = count('pending') + count('unknown');
    const message = rows.find((r) => r.message)?.message;

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatPill label="Recipients" value={rows.length.toLocaleString()} />
                <StatPill label="Delivered" value={count('good').toLocaleString()} />
                <StatPill label="Awaiting" value={waiting.toLocaleString()} />
                <StatPill label="Not delivered" value={failed.toLocaleString()} />
            </div>

            {message && (
                <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Message</p>
                    <p className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words max-h-28 overflow-auto">{message}</p>
                </div>
            )}

            <div className="max-h-72 overflow-auto rounded-lg border border-gray-100 dark:border-gray-700">
                <table className="min-w-full text-sm">
                    <thead className="sticky top-0 text-xs uppercase tracking-wide text-gray-400 bg-gray-50 dark:bg-gray-900">
                        <tr>
                            <th className="px-4 py-2.5 text-left font-medium">Recipient</th>
                            <th className="px-4 py-2.5 text-left font-medium">Status</th>
                            <th className="px-4 py-2.5 text-left font-medium">Sent</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                        {sorted.map((r, i) => (
                            <tr key={r._id ?? r.message_id ?? i}>
                                <td className="px-4 py-2.5 tabular-nums text-gray-800 dark:text-gray-100 whitespace-nowrap">{localPhone(r.recipient)}</td>
                                <td className="px-4 py-2.5 whitespace-nowrap">
                                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${TONE_CLASS[r.st.tone]}`}>{r.st.label}</span>
                                    {Number(r.retries) > 0 && <span className="ml-2 text-xs text-gray-400">{r.retries} {Number(r.retries) === 1 ? 'retry' : 'retries'}</span>}
                                </td>
                                <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">{sentAt(r.date_sent)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function HistoryTable({ history, onReport }) {
    if (history === null) return <div className="py-10 flex justify-center"><Loader /></div>;
    if (history.length === 0) return <p className="text-sm text-gray-400 py-8 text-center">No SMS campaigns yet.</p>;

    const statusColor = (s) => s === 'sent' ? 'text-emerald-600' : s === 'scheduled' ? 'text-amber-500' : 'text-red-500';

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
            <table className="min-w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40">
                    <tr>
                        <th className="px-4 py-3 text-left">Date</th>
                        <th className="px-4 py-3 text-left">Message</th>
                        <th className="px-4 py-3 text-right">Recipients</th>
                        <th className="px-4 py-3 text-right">Credits</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left">Provider</th>
                        <th className="px-4 py-3 text-left">By</th>
                        <th className="px-4 py-3"></th>
                    </tr>
                </thead>
                <tbody>
                    {history.map((c) => (
                        <tr key={c._id} className="border-b border-gray-50 dark:border-gray-700/50">
                            <td className="px-4 py-3 whitespace-nowrap text-gray-500">{new Date(c.createdAt).toLocaleString('en-GB')}</td>
                            <td className="px-4 py-3 max-w-xs truncate text-gray-700 dark:text-gray-200">{c.message}</td>
                            <td className="px-4 py-3 text-right tabular-nums">{c.recipientCount}</td>
                            <td className="px-4 py-3 text-right tabular-nums">{c.creditUsed}</td>
                            <td className={`px-4 py-3 font-medium ${statusColor(c.status)}`}>{c.status}</td>
                            <td className="px-4 py-3 text-gray-500">{({ mnotify: 'mNotify', arkesel: 'Arkesel' }[c.provider] || c.provider || 'mNotify')}</td>
                            <td className="px-4 py-3 text-gray-500">{c.requestedBy?.name || c.requestedBy?.email || '—'}</td>
                            <td className="px-4 py-3 text-right">
                                {c.mnotifyCampaignId && (
                                    <button className="text-xs text-[#1e3a6e] dark:text-blue-400 hover:underline" onClick={() => onReport(c._id)}>Report</button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default SendSMS;
